import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ActivityIndicator,
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
} from 'react-native'

import { useNavigation, useRoute } from '@react-navigation/native'
import { useGetZetCashMarketplaceBanner } from '@zet/customer/src/api/hooks/webFlow'
import { Button } from '@zet/design-system/button'
import { Icon } from '@zet/design-system/icons'
import { Stack, XStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import {
  getUserFlowSession,
  setUserFlowRoute,
  setUserFlowSession,
} from '@zet/utils/userFlowSession'
import { LinearGradient } from 'expo-linear-gradient'

import ScreenViewEventWrapper from '../../components/screenViewEventWrapper'
import {
  RecommendationSection,
  UserFlowLoading,
  UserFlowRecommendationCard,
  useUserFlowApplyLink,
} from '../../components/userFlow'
import {
  UserFlowCustomerLead,
  UserFlowCustomerLeadStatus,
  UserFlowPartnerProduct,
  useUserFlowPartnerProducts,
  useUserFlowRecommendations,
} from '../userFlow/utils'

type UserFlowHomeRouteParams = {
  customerId?: number
}

export const primaryButtonStyle = {
  backgroundColor: '#832DC2',
  borderColor: '#7929B4',
  borderWidth: 1.2,
  borderRadius: 12,
  minHeight: 40,
  marginTop: 12,
}

const MAX_VISIBLE_EXISTING_LEADS = 3
const brandGradients = [
  ['#FFF6D1', '#FFFFFF'],
  ['#FDE3D6', '#FFFFFF'],
  ['#DFFFEF', '#FFFFFF'],
]

const getBrandGradient = (index: number) =>
  brandGradients[index % brandGradients.length]

const gradientStart = { x: 0, y: 0 }
const gradientEnd = { x: 1, y: 1 }
const gradientVerticalEnd = { x: 0, y: 1 }

const zetCashBadge = (logoUrl?: string) =>
  logoUrl ? (
    <Image
      source={{ uri: logoUrl }}
      style={styles.zetCashBadge}
      resizeMode="contain"
    />
  ) : null

const getDisplayName = (name?: string) => (name || 'Recommended offer').trim()

type StatusConfig = {
  textColor: string
  backgroundColor: string
  borderColor: string
}

const LEAD_STATUS = {
  REJECTED: 'rejected',
  LEAD_ADDED: 'leadadded',
  LEAD_IN_PROGRESS: 'actnreqrd',
  EXPIRED: 'expired',
  FULLFILLED: 'fullfilled',
  FULFILLED: 'fulfilled',
} as const

const STATUS_COLOR_CONFIG: Record<UserFlowCustomerLeadStatus, StatusConfig> = {
  [LEAD_STATUS.REJECTED]: {
    textColor: '#B42318',
    backgroundColor: '#FEF3F2',
    borderColor: '#FECDCA',
  },
  [LEAD_STATUS.LEAD_ADDED]: {
    textColor: '#C2410C',
    backgroundColor: '#FFF7ED',
    borderColor: '#FFEDD5',
  },
  [LEAD_STATUS.LEAD_IN_PROGRESS]: {
    textColor: '#C2410C',
    backgroundColor: '#FFF7ED',
    borderColor: '#FFEDD5',
  },
  [LEAD_STATUS.EXPIRED]: {
    textColor: '#374151',
    backgroundColor: '#F3F4F6',
    borderColor: '#D1D5DB',
  },
  [LEAD_STATUS.FULFILLED]: {
    textColor: '#249410',
    backgroundColor: '#F8FCF7',
    borderColor: '#AFE3A6',
  },
  [LEAD_STATUS.FULLFILLED]: {
    textColor: '#249410',
    backgroundColor: '#F8FCF7',
    borderColor: '#AFE3A6',
  },
} as const

const formatLeadDate = (date?: string) => {
  if (!date) {
    return ''
  }

  const parsedDate = new Date(date)

  if (Number.isNaN(parsedDate.getTime())) {
    return date
  }

  return parsedDate.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

const getLeadCtaText = (status?: UserFlowCustomerLeadStatus) =>
  status === LEAD_STATUS.LEAD_ADDED
    ? 'Start Application'
    : status === LEAD_STATUS.LEAD_IN_PROGRESS
      ? 'Resume Application'
      : status === LEAD_STATUS.EXPIRED
        ? 'Restart Application'
        : undefined

const ExistingLeadCard = ({
  product,
  lead,
  customerId,
  onApplySuccess,
  index,
}: {
  product: {
    id?: number
    partnerId?: number
    name?: string
    imageUrl?: string
    backgroundGradient?: string[]
  }
  lead?: UserFlowCustomerLead
  customerId: number
  onApplySuccess?: () => void
  index: number
}) => {
  const { openGeneratedLink, isLoading } = useUserFlowApplyLink(onApplySuccess)
  const status = lead?.status || LEAD_STATUS.LEAD_ADDED
  const statusConfig =
    STATUS_COLOR_CONFIG[status] || STATUS_COLOR_CONFIG[LEAD_STATUS.LEAD_ADDED]
  const ctaText = getLeadCtaText(status)
  const formattedDate = formatLeadDate(lead?.createdDate)
  const handleApplyPress = async () => {
    await openGeneratedLink({
      partnerId: Number(product?.partnerId),
      customerId,
      variantId: product?.id,
    })
  }

  return (
    <LinearGradient
      colors={[
        product?.backgroundGradient?.[0] || getBrandGradient(index)[0],
        '#FFFDF7',
        '#FFFFFF',
        '#FFFFFF',
      ]}
      locations={[0, 0.24, 0.5, 1]}
      start={gradientStart}
      end={gradientEnd}
      style={styles.existingLeadGradient}
    >
      <Stack style={styles.existingLeadContent}>
        <XStack className="items-start justify-between">
          <Stack className="flex-1 pr-3">
            <Text style={styles.existingLeadTitle}>
              {getDisplayName(product?.name)}
            </Text>
          </Stack>
          {product?.imageUrl ? (
            <Image
              source={{ uri: product.imageUrl }}
              style={styles.existingLeadLogo}
              resizeMode="contain"
            />
          ) : null}
        </XStack>
        <Text style={styles.existingLeadDateContainer}>
          <Text style={styles.existingLeadDate}>
            {formattedDate ? `Application started on ${formattedDate}` : ''}
          </Text>
        </Text>
        <Stack
          style={[
            styles.existingLeadStatusContainer,
            {
              borderColor: statusConfig.borderColor,
              backgroundColor: statusConfig.backgroundColor,
            },
          ]}
        >
          <Text
            style={[
              styles.existingLeadStatusTitle,
              { color: statusConfig.textColor },
            ]}
          >
            {lead?.currentStatusTitle || ''}
          </Text>
          <Text style={styles.existingLeadStatusMessage}>
            {lead?.currentStatusMessage || ''}
          </Text>
        </Stack>
        {ctaText ? (
          <Button
            size="medium"
            loading={isLoading}
            onPress={handleApplyPress}
            style={primaryButtonStyle}
            textStyle=""
          >
            <Text style={styles.existingLeadButtonText}>{ctaText}</Text>
          </Button>
        ) : null}
      </Stack>
    </LinearGradient>
  )
}

const UserHome = () => {
  const route = useRoute() as { params?: UserFlowHomeRouteParams }
  const navigation = useNavigation()
  const session = getUserFlowSession()
  const customerId = route?.params?.customerId || session?.customerId
  const {
    isLoading,
    agentData,
    customerId: resolvedCustomerId,
    freshProducts,
    customerLeads,
    totalProductCount,
    paginationMeta,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  } = useUserFlowRecommendations(Number(customerId))
  const hasExistingLeads = (customerLeads?.length || 0) > 0
  const { data: bannerData } = useGetZetCashMarketplaceBanner({
    onecode: agentData?.onecode || '',
  })
  const { data: partnerProducts = [] } =
    useUserFlowPartnerProducts(hasExistingLeads)
  const [showAllExistingLeads, setShowAllExistingLeads] = useState(false)
  const [showAllFreshOffers, setShowAllFreshOffers] = useState(false)
  const isRedirectingToLoginRef = useRef(false)
  const customerLeadMap = useMemo(
    () =>
      new Map(
        customerLeads?.map((lead) => [Number(lead?.partnerId), lead] as const),
      ),
    [customerLeads],
  )
  const partnerProductMap = useMemo(
    () =>
      new Map(
        partnerProducts?.map((product) => [
          Number(product?.partnerId),
          product,
        ]) || [],
      ),
    [partnerProducts],
  )
  const existingLeadCards = useMemo(
    () =>
      customerLeads?.map((lead) => {
        const partnerProduct = partnerProductMap?.get(Number(lead?.partnerId))

        const product: UserFlowPartnerProduct = {
          partnerId: Number(lead?.partnerId),
          name: partnerProduct?.name,
          imageUrl: partnerProduct?.imageUrl,
          backgroundGradient: partnerProduct?.backgroundGradient,
        }

        return { lead, product }
      }) || [],
    [customerLeads, partnerProductMap],
  )
  const visibleExistingLeadCards = useMemo(
    () =>
      showAllExistingLeads
        ? existingLeadCards
        : existingLeadCards?.slice(0, MAX_VISIBLE_EXISTING_LEADS),
    [existingLeadCards, showAllExistingLeads],
  )
  const shouldShowExistingLeadToggle =
    existingLeadCards?.length > MAX_VISIBLE_EXISTING_LEADS
  const noExistingLeadProducts = freshProducts
  const horizontalOfferProducts = freshProducts?.slice(0, 3) || []
  const visibleFreshProducts = showAllFreshOffers
    ? noExistingLeadProducts
    : noExistingLeadProducts?.slice(0, 3)
  const handleUserHomeApplySuccess = () => {
    refetch?.()
  }

  useEffect(() => {
    setUserFlowRoute('UserHome')
  }, [])

  useEffect(() => {
    const redirectToUserLogin = () => {
      if (isRedirectingToLoginRef.current) {
        return
      }

      isRedirectingToLoginRef.current = true
      setUserFlowSession({
        lastRoute: 'UserLogin',
      })
      navigation.replace('UserLogin' as never)
    }

    const unsubscribe = navigation.addListener('beforeRemove', (event) => {
      if (isRedirectingToLoginRef.current) {
        return
      }

      event.preventDefault()
      redirectToUserLogin()
    })

    return unsubscribe
  }, [navigation])

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
      return
    }

    const currentUrl = new URL(window.location.href)
    const hash = currentUrl.searchParams.get('hash')
    const loginUrl = hash
      ? `/user/login?hash=${encodeURIComponent(hash)}`
      : '/user/login'

    const handlePopState = () => {
      if (isRedirectingToLoginRef.current) {
        return
      }

      isRedirectingToLoginRef.current = true
      setUserFlowSession({
        lastRoute: 'UserLogin',
      })
      window.history.pushState(null, '', loginUrl)
      navigation.replace('UserLogin' as never)
    }

    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [navigation])

  useEffect(() => {
    if (!customerId) {
      navigation.replace('UserLogin')
    }
  }, [customerId, navigation])

  if (isLoading) {
    return <UserFlowLoading />
  }

  if (!hasExistingLeads) {
    return (
      <ScreenViewEventWrapper screenName="user_flow_home">
        <ScrollView
          className="flex-1 bg-white"
          contentContainerStyle={styles.offersScrollContent}
          showsVerticalScrollIndicator={false}
        >
          <Stack className="px-4 pb-6 pt-9">
            <Text style={styles.offersTitle}>{`Loan Offers for You`}</Text>
          </Stack>
          <Stack style={styles.offersCardsContainer}>
            {visibleFreshProducts?.map((item, index) => (
              <Stack
                key={`${item?.partnerId}-${item?.id}-${index}`}
                style={[
                  styles.offersCardWrapper,
                  index === visibleFreshProducts?.length - 1 &&
                    styles.offersCardWrapperLast,
                ]}
              >
                <UserFlowRecommendationCard
                  product={item}
                  customerId={resolvedCustomerId}
                  onApplySuccess={handleUserHomeApplySuccess}
                  index={index}
                />
              </Stack>
            ))}
            {(totalProductCount || 0) > 3 ||
            (noExistingLeadProducts?.length || 0) > 3 ? (
              <Pressable
                onPress={() => {
                  if (
                    !showAllFreshOffers &&
                    hasNextPage &&
                    !isFetchingNextPage
                  ) {
                    fetchNextPage()
                  }
                  setShowAllFreshOffers((currentValue) => !currentValue)
                }}
              >
                <XStack style={styles.toggleContainer}>
                  {isFetchingNextPage ? (
                    <ActivityIndicator
                      size="small"
                      color="#832DC2"
                      style={styles.loader}
                    />
                  ) : (
                    <>
                      <Text style={styles.toggleText}>
                        {showAllFreshOffers ? 'View Less' : 'View All'}
                      </Text>
                      <Icon
                        type="ArrowDown"
                        size={24}
                        color="#832DC2"
                        style={[
                          styles.toggleIcon,
                          showAllFreshOffers && styles.toggleIconExpanded,
                        ]}
                      />
                    </>
                  )}
                </XStack>
              </Pressable>
            ) : null}
          </Stack>
        </ScrollView>
      </ScreenViewEventWrapper>
    )
  }

  return (
    <ScreenViewEventWrapper screenName="user_flow_home">
      <ScrollView
        className="flex-1 bg-white"
        contentContainerStyle={styles.homeScrollContent}
      >
        <LinearGradient
          colors={[bannerData?.gradientColor || '#EFFAD9', '#FFFFFF']}
          start={gradientStart}
          end={gradientVerticalEnd}
        >
          <Stack
            className="overflow-hidden px-4 pb-4 pt-[18px]"
            style={styles.heroContent}
          >
            {zetCashBadge(bannerData?.logoUrl)}
            <Text style={styles.heroTitle}>Your Loan Application</Text>
            <Stack style={styles.existingLeadCardsContainer}>
              {visibleExistingLeadCards?.map((item, index) => {
                const lead =
                  customerLeadMap?.get(Number(item?.product?.partnerId)) ||
                  item?.lead
                if (!item?.product?.name) return null

                return (
                  <ExistingLeadCard
                    key={`${item?.product?.partnerId}-${item?.product?.id}-${index}`}
                    product={item.product}
                    lead={lead}
                    customerId={resolvedCustomerId}
                    onApplySuccess={handleUserHomeApplySuccess}
                    index={index}
                  />
                )
              })}
            </Stack>
            {shouldShowExistingLeadToggle ? (
              <Pressable
                onPress={() =>
                  setShowAllExistingLeads((currentValue) => !currentValue)
                }
              >
                <XStack style={styles.existingLeadToggleContainer}>
                  <Text style={styles.toggleText}>
                    {showAllExistingLeads ? 'View Less' : 'View All'}
                  </Text>
                  <Icon
                    type="ArrowDown"
                    size={24}
                    color="#832DC2"
                    style={[
                      styles.toggleIcon,
                      showAllExistingLeads && styles.toggleIconExpanded,
                    ]}
                  />
                </XStack>
              </Pressable>
            ) : null}
          </Stack>
        </LinearGradient>
        {freshProducts?.length || 0 ? (
          <Stack className="bg-white pb-4">
            <XStack className="items-center gap-4 px-4">
              <Stack style={styles.separatorLine} />
              <Text style={styles.separatorTitle}>
                Other Loan Offers For You
              </Text>
              <Stack style={styles.separatorLine} />
            </XStack>
            <RecommendationSection
              title=""
              subtitle=""
              products={horizontalOfferProducts}
              customerId={resolvedCustomerId}
              onApplySuccess={handleUserHomeApplySuccess}
              horizontal={true}
            />
            <Pressable
              onPress={() => {
                setUserFlowRoute('UserHome')
                navigation.navigate('UserOffers' as never)
              }}
            >
              <XStack className="mt-4 items-center justify-center">
                <Text style={styles.toggleText}>View All</Text>
                <Icon type="ArrowRight" size={24} color="#832DC2" />
              </XStack>
            </Pressable>
          </Stack>
        ) : null}
      </ScrollView>
    </ScreenViewEventWrapper>
  )
}

const styles = StyleSheet.create({
  existingLeadButton: {
    ...primaryButtonStyle,
    marginTop: 12,
  },
  existingLeadButtonText: {
    fontFamily: 'THICCCBOI-SemiBold',
    fontSize: 14,
    lineHeight: 24,
    letterSpacing: -0.28,
    color: '#FFFFFF',
  },
  existingLeadCardsContainer: {
    marginTop: 16,
  },
  existingLeadContent: {
    paddingHorizontal: 16,
    paddingVertical: 18,
  },
  existingLeadDate: {
    fontFamily: 'THICCCBOI-Medium',
    fontSize: 12,
    lineHeight: 18,
    letterSpacing: -0.24,
    color: '#4B5563',
  },
  existingLeadDateContainer: {
    marginTop: 6,
  },
  existingLeadGradient: {
    width: '100%',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    overflow: 'hidden',
    marginBottom: 16,
  },
  existingLeadLogo: {
    height: 32,
    width: 104,
  },
  existingLeadStatusContainer: {
    marginTop: 16,
    borderRadius: 8,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  existingLeadStatusMessage: {
    marginTop: 4,
    fontFamily: 'THICCCBOI-Medium',
    fontSize: 12,
    lineHeight: 18,
    letterSpacing: -0.24,
    color: '#4B5563',
  },
  existingLeadStatusTitle: {
    fontFamily: 'THICCCBOI-SemiBold',
    fontSize: 13,
    lineHeight: 19,
    letterSpacing: -0.26,
  },
  existingLeadTitle: {
    fontFamily: 'THICCCBOI-ExtraBold',
    fontSize: 16,
    lineHeight: 22,
    letterSpacing: -0.32,
    color: '#0C0E11',
  },
  existingLeadToggleContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 4,
  },
  heroContent: {
    marginTop: 16,
  },
  heroTitle: {
    marginTop: 40,
    fontFamily: 'THICCCBOI-ExtraBold',
    fontSize: 18,
    lineHeight: 24,
    letterSpacing: -0.36,
    color: '#0C0E11',
  },
  homeScrollContent: {
    paddingBottom: 32,
  },
  loader: {
    marginTop: 18,
  },
  offersCardsContainer: {
    paddingHorizontal: 16,
  },
  offersCardWrapper: {
    marginBottom: 18,
  },
  offersCardWrapperLast: {
    marginBottom: 0,
  },
  offersScrollContent: {
    paddingBottom: 40,
    paddingTop: 40,
  },
  offersTitle: {
    fontFamily: 'THICCCBOI-ExtraBold',
    fontSize: 20,
    lineHeight: 26,
    letterSpacing: -0.4,
    color: '#0C0E11',
  },
  separatorLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#EFECF2',
  },
  separatorTitle: {
    marginHorizontal: 8,
    fontFamily: 'THICCCBOI-ExtraBold',
    fontSize: 15,
    lineHeight: 20,
    letterSpacing: -0.3,
    color: '#374151',
  },
  toggleContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 20,
  },
  toggleIcon: {
    marginLeft: 4,
    transform: [{ rotate: '0deg' }],
  },
  toggleIconExpanded: {
    transform: [{ rotate: '180deg' }],
  },
  toggleText: {
    fontFamily: 'THICCCBOI-SemiBold',
    fontSize: 14,
    lineHeight: 24,
    letterSpacing: -0.28,
    color: '#832DC2',
  },
  zetCashBadge: {
    width: 113,
    height: 24,
  },
})

export default UserHome
